"use client";

import { useState, useEffect, useCallback } from "react";
import {
  MoreVertical,
  Edit,
  Trash2,
  ChevronDown,
  Star,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  MessageSquare,
  Check,
  Dog,
  Cat,
  Fish,
  Bird,
  Rabbit,
  Turtle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  fetchAppointments,
  updateAppointmentStatus,
  updateAppointment,
  deleteAppointment,
  calculateAppointmentStats,
  filterAppointments,
  getPetTypeLabel,
  getPaginatedAppointments,
} from "../utils/appointmentsUtils";
import { Button } from "../components/ui/button";
import { Modal } from "../components/ui/modal";
import { Dropdown, DropdownItem } from "../components/ui/dropdown";
import { Tabs } from "../components/ui/tabs";
import { StatCard } from "../components/ui/stat-card";
import { SearchBar } from "../components/ui/search-bar";
import { DateRangeFilter } from "../components/ui/date-range-filter";

const apiUrl = process.env.REACT_APP_API_URL;

const PET_TYPE_ICONS = {
  dog: Dog,
  cat: Cat,
  fish: Fish,
  bird: Bird,
  rabbit: Rabbit,
  turtle: Turtle,
  rodent: Star,
};

const AppointmentsScreen = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [stats, setStats] = useState({
    todayCount: 0,
    upcomingNext: "--:--",
    confirmed: 0,
    confirmedTrend: "0%",
    confirmedTrendDirection: "neutral",
    pending: 0,
    pendingTrend: "0%",
    pendingTrendDirection: "neutral",
    cancelled: 0,
    cancelledTrend: "0%",
    cancelledTrendDirection: "neutral",
  });

  const itemsPerPage = 7;

  const loadAppointments = useCallback(async () => {
    if (!token || !user?.veterinary?.id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchAppointments(token, user.veterinary.id);
      setAppointments(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    const newStats = calculateAppointmentStats(appointments);
    setStats(newStats);
  }, [appointments]);

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const appointment = appointments.find((a) => a.id === appointmentId);
      await updateAppointmentStatus(
        token,
        appointmentId,
        appointment.service.id,
        newStatus,
      );
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === appointmentId ? { ...a, status: newStatus } : a,
        ),
      );
    } catch (err) {
      setError("Failed to update appointment status.");
    }
  };

  const handleEditAppointment = async (
    appointmentId,
    newStatus,
    newComments,
  ) => {
    try {
      const appointment = appointments.find((a) => a.id === appointmentId);
      await updateAppointment(token, appointmentId, appointment.service.id, {
        status: newStatus,
        comments: newComments,
      });
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === appointmentId
            ? { ...a, status: newStatus, comments: newComments }
            : a,
        ),
      );
    } catch (err) {
      setError("Failed to update appointment status.");
    }
  };

  const handleDeleteAppointment = async (appointment) => {
    try {
      await deleteAppointment(token, appointment.id, appointment.service.id);
      setAppointments((prev) => prev.filter((a) => a.id !== appointment.id));
    } catch (err) {
      setError("Failed to delete appointment.");
    }
  };

  const handleMessageClick = async (appointment) => {
    try {
      const recipientId = appointment.pet?.user?.id;

      if (!recipientId) {
        setError("Unable to find pet owner information");
        return;
      }

      const response = await axios.post(
        `${apiUrl}/api/v1/conversations`,
        {
          conversation: {
            recipient_id: recipientId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const conversationPath = `/chats?conversation_id=${response.data.id}`;
      navigate(conversationPath);
    } catch (err) {
      console.error("Error creating/finding conversation:", err);
      setError("Failed to open conversation. Please try again.");
    }
  };

  const filteredAppointments = filterAppointments(
    appointments,
    searchQuery,
    activeFilter,
    dateRange,
  );

  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const currentAppointments = getPaginatedAppointments(
    filteredAppointments,
    currentPage,
    itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchQuery, dateRange]);

  const StatusDropdown = ({ status, onChange, appointmentId }) => {
    const [isOpen, setIsOpen] = useState(false);

    const statusConfig = {
      pending: {
        label: "Pending",
        color: "#F97316",
        bg: "transparent",
        textColor: "#F97316",
      },
      confirmed: {
        label: "Confirmed",
        color: "#10B981",
        bg: "transparent",
        textColor: "#10B981",
      },
      completed: {
        label: "Completed",
        color: "#6B7280",
        bg: "transparent",
        textColor: "#6B7280",
        icon: Check,
      },
      cancelled: {
        label: "Cancelled",
        color: "#EF4444",
        bg: "#FEE2E2",
        textColor: "#EF4444",
      },
    };

    const currentStatus = statusConfig[status] || statusConfig.pending;

    return (
      <div style={{ position: "relative" }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "6px 12px",
            borderRadius: "6px",
            border: "none",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            backgroundColor: currentStatus.bg,
            color: currentStatus.textColor,
          }}
        >
          {currentStatus.icon && (
            <currentStatus.icon style={{ width: "14px", height: "14px" }} />
          )}
          {currentStatus.label}
          <ChevronDown style={{ width: "14px", height: "14px" }} />
        </button>
        {isOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              marginTop: "4px",
              backgroundColor: "#fff",
              borderRadius: "6px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
              zIndex: 20,
              minWidth: "120px",
            }}
          >
            {Object.entries(statusConfig).map(([key, config]) => (
              <div
                key={key}
                onClick={() => {
                  onChange(appointmentId, key);
                  setIsOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "background-color 150ms",
                  backgroundColor: status === key ? "#F3F4F6" : "transparent",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#F3F4F6")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    status === key ? "#F3F4F6" : "transparent")
                }
              >
                <span style={{ color: config.color }}>{config.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleRowClick = (appointment) => {
    setSelectedAppointment(appointment);
    setIsDetailModalOpen(true);
  };

  const s = {
    container: {
      padding: "24px",
      minHeight: "100vh",
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      paddingBottom: "16px",
      borderBottom: "1px solid #E5E7EB",
    },
    headerTitle: {
      fontSize: "24px",
      fontWeight: "600",
      color: "#111827",
      margin: 0,
    },
    error: {
      padding: "12px",
      marginBottom: "16px",
      backgroundColor: "#FEE2E2",
      color: "#EF4444",
      borderRadius: "8px",
      fontSize: "14px",
    },
    statsRow: {
      display: "flex",
      gap: "16px",
      marginBottom: "24px",
      flexWrap: "wrap",
    },
    statsCard: {
      display: "flex",
      alignItems: "center",
      padding: "16px 20px",
      backgroundColor: "#fff",
      borderRadius: "12px",
      border: "1px solid #E5E7EB",
      gap: "0",
    },
    statsCardLeft: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      paddingRight: "20px",
    },
    statsCardRight: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      paddingLeft: "20px",
    },
    statsDivider: { width: "1px", height: "48px", backgroundColor: "#E5E7EB" },
    statsIconWrapper: {
      width: "40px",
      height: "40px",
      borderRadius: "8px",
      backgroundColor: "#F3F4F6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    statsIcon: { width: "20px", height: "20px", color: "#6B7280" },
    statsLabel: { fontSize: "12px", color: "#6B7280", marginBottom: "4px" },
    statsValue: { fontSize: "20px", fontWeight: "700", color: "#111827" },
    listSection: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      border: "1px solid #E5E7EB",
      padding: "20px",
    },
    sectionTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#111827",
      margin: "0 0 16px 0",
    },
    filtersRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
      flexWrap: "wrap",
      gap: "12px",
    },
    searchRow: { display: "flex", gap: "12px", alignItems: "center" },
    tableContainer: { overflowX: "auto", overflow: "visible" },
    table: { width: "100%", borderCollapse: "collapse" },
    tableHeader: { borderBottom: "1px solid #E5E7EB" },
    tableHeaderCell: {
      padding: "12px 16px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: "500",
      color: "#6B7280",
      textTransform: "capitalize",
    },
    tableRow: { borderBottom: "1px solid #F3F4F6" },
    tableCell: { padding: "16px", verticalAlign: "middle" },
    tableCellText: {
      padding: "16px",
      verticalAlign: "middle",
      fontSize: "14px",
      color: "#374151",
    },
    petCell: { display: "flex", alignItems: "center", gap: "12px" },
    petAvatarPlaceholder: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "#F3F4F6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    petAvatarIcon: { width: "20px", height: "20px", color: "#9CA3AF" },
    petName: { fontSize: "14px", fontWeight: "500", color: "#111827" },
    petSpecies: { fontSize: "12px", color: "#6B7280" },
    orderName: {
      fontSize: "14px",
      color: "#111827",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    additionalBadge: { fontSize: "12px", color: "#F97316", fontWeight: "500" },
    orderNumber: { fontSize: "12px", color: "#6B7280" },
    dateText: { fontSize: "14px", color: "#111827" },
    timeText: { fontSize: "12px", color: "#6B7280" },
    messageWrapper: {
      position: "relative",
      display: "inline-flex",
      cursor: "pointer",
      transition: "opacity 0.2s",
    },
    messageIcon: { width: "20px", height: "20px", color: "#9CA3AF" },
    messageBadge: {
      position: "absolute",
      top: "-6px",
      right: "-6px",
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      backgroundColor: "#EF4444",
      color: "#fff",
      fontSize: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "600",
    },
    loadingMessage: {
      padding: "40px",
      textAlign: "center",
      color: "#6B7280",
      fontSize: "14px",
    },
    errorMessage: {
      padding: "40px",
      textAlign: "center",
      color: "#EF4444",
      fontSize: "14px",
    },
    formGroup: { marginBottom: "16px" },
    formLabel: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "6px",
    },
    formSelect: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #E5E7EB",
      borderRadius: "6px",
      fontSize: "14px",
      outline: "none",
    },
    formTextarea: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #E5E7EB",
      borderRadius: "6px",
      fontSize: "14px",
      outline: "none",
      minHeight: "80px",
      resize: "vertical",
      boxSizing: "border-box",
    },
    formActions: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      marginTop: "24px",
    },
  };

  if (!token)
    return (
      <div style={s.container}>
        <div style={s.errorMessage}>Please log in to view appointments.</div>
      </div>
    );

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.headerTitle}>Appointments</h1>
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.statsRow}>
        <div style={s.statsCard}>
          <div style={s.statsCardLeft}>
            <div style={s.statsIconWrapper}>
              <Star style={s.statsIcon} />
            </div>
            <div>
              <div style={s.statsLabel}>Today's Appointment</div>
              <div style={s.statsValue}>{stats.todayCount}</div>
            </div>
          </div>
          <div style={s.statsDivider} />
          <div style={s.statsCardRight}>
            <div style={s.statsIconWrapper}>
              <Clock style={s.statsIcon} />
            </div>
            <div>
              <div style={s.statsLabel}>Upcoming Next</div>
              <div style={s.statsValue}>{stats.upcomingNext}</div>
            </div>
          </div>
        </div>

        <StatCard
          icon={CheckCircle2}
          title="Confirmed Request"
          value={stats.confirmed}
          trend={stats.confirmedTrend}
          trendDirection={stats.confirmedTrendDirection}
          variant="success"
        />
        <StatCard
          icon={AlertCircle}
          title="Pending request"
          value={stats.pending}
          trend={stats.pendingTrend}
          trendDirection={stats.pendingTrendDirection}
          variant="warning"
        />
        <StatCard
          icon={XCircle}
          title="Canceled Request"
          value={stats.cancelled}
          trend={stats.cancelledTrend}
          trendDirection={stats.cancelledTrendDirection}
          variant="danger"
        />
      </div>

      <div style={s.listSection}>
        <h2 style={s.sectionTitle}>List of Appointment</h2>

        <div style={s.filtersRow}>
          <Tabs
            filters={[
              "All",
              "Upcoming today",
              "Confirmed",
              "Pending",
              "Completed",
              "Cancelled",
            ]}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />
          <div style={s.searchRow}>
            <div style={{ width: "200px" }}>
              <SearchBar
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
              />
            </div>
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              placeholder="Select dates"
            />
          </div>
        </div>

        {loading ? (
          <div style={s.loadingMessage}>Loading appointments...</div>
        ) : (
          <>
            <div style={s.tableContainer}>
              <table style={s.table}>
                <thead>
                  <tr style={s.tableHeader}>
                    <th style={s.tableHeaderCell}>Name</th>
                    <th style={s.tableHeaderCell}>Owner</th>
                    <th style={s.tableHeaderCell}>Order</th>
                    <th style={s.tableHeaderCell}>Created</th>
                    <th style={s.tableHeaderCell}>Status</th>
                    <th style={s.tableHeaderCell}>Message</th>
                    <th style={{ ...s.tableHeaderCell, textAlign: "right" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentAppointments.map((appointment) => {
                    const PetIcon =
                      PET_TYPE_ICONS[
                        appointment.pet?.pet_type?.toLowerCase()
                      ] || Star;
                    return (
                      <tr
                        key={appointment.id}
                        style={{ ...s.tableRow, cursor: "pointer" }}
                        onClick={() => handleRowClick(appointment)}
                      >
                        <td style={s.tableCell}>
                          <div style={s.petCell}>
                            <div style={s.petAvatarPlaceholder}>
                              <PetIcon style={s.petAvatarIcon} />
                            </div>
                            <div>
                              <div style={s.petName}>
                                {appointment.pet?.name || "Unknown"}
                              </div>
                              <div style={s.petSpecies}>
                                {getPetTypeLabel(appointment.pet?.pet_type)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={s.tableCellText}>
                          {`${appointment.pet?.user?.first_name || ""} ${appointment.pet?.user?.last_name || ""}`.trim() ||
                            "Unknown"}
                        </td>
                        <td style={s.tableCell}>
                          <div>
                            <div style={s.orderName}>
                              {appointment.service?.name || "Service"}
                              {appointment.additionalServices > 0 && (
                                <span style={s.additionalBadge}>
                                  +{appointment.additionalServices}
                                </span>
                              )}
                            </div>
                            <div style={s.orderNumber}>#{appointment.id}</div>
                          </div>
                        </td>
                        <td style={s.tableCell}>
                          <div>
                            <div style={s.dateText}>
                              {new Date(
                                appointment.datetime,
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </div>
                            <div style={s.timeText}>
                              {new Date(
                                appointment.datetime,
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </div>
                        </td>
                        <td
                          style={s.tableCell}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <StatusDropdown
                            status={appointment.status}
                            onChange={handleStatusChange}
                            appointmentId={appointment.id}
                          />
                        </td>
                        <td
                          style={s.tableCell}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMessageClick(appointment);
                          }}
                        >
                          <div
                            style={s.messageWrapper}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = "0.7";
                              e.currentTarget.querySelector("svg").style.color =
                                "var(--primary)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = "1";
                              e.currentTarget.querySelector("svg").style.color =
                                "var(--icon)";
                            }}
                            title="Open conversation with pet owner"
                          >
                            <MessageSquare style={s.messageIcon} />
                            {appointment.messages > 0 && (
                              <span style={s.messageBadge}>
                                {appointment.messages}
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          style={{ ...s.tableCell, textAlign: "right" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Dropdown
                            trigger={
                              <Button
                                variant="ghost"
                                icon={MoreVertical}
                                size="small"
                              />
                            }
                          >
                            <DropdownItem
                              icon={Edit}
                              onClick={() => {
                                setEditingAppointment(appointment);
                                setIsEditModalOpen(true);
                              }}
                            >
                              Edit
                            </DropdownItem>
                            <DropdownItem
                              icon={Trash2}
                              variant="destructive"
                              onClick={() =>
                                handleDeleteAppointment(appointment)
                              }
                            >
                              Delete
                            </DropdownItem>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {currentAppointments.length === 0 && (
              <div style={s.loadingMessage}>No appointments found.</div>
            )}
            {totalPages > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "16px",
                  padding: "12px 0",
                }}
              >
                <div style={{ fontSize: "14px", color: "#6B7280" }}>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(
                    currentPage * itemsPerPage,
                    filteredAppointments.length,
                  )}{" "}
                  of {filteredAppointments.length} appointments
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft style={{ width: "16px", height: "16px" }} />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        variant={currentPage === page ? "outline" : "ghost"}
                      >
                        {page}
                      </Button>
                    ),
                  )}
                  <Button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight style={{ width: "16px", height: "16px" }} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingAppointment(null);
        }}
        title="Edit Appointment"
      >
        {editingAppointment && (
          <div>
            <div style={s.formGroup}>
              <label style={s.formLabel}>Status</label>
              <select
                value={editingAppointment.status}
                onChange={(e) =>
                  setEditingAppointment({
                    ...editingAppointment,
                    status: e.target.value,
                  })
                }
                style={s.formSelect}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.formLabel}>Comments</label>
              <textarea
                value={editingAppointment.comments || ""}
                onChange={(e) =>
                  setEditingAppointment({
                    ...editingAppointment,
                    comments: e.target.value,
                  })
                }
                style={s.formTextarea}
                placeholder="Add comments..."
              />
            </div>
            <div style={s.formActions}>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingAppointment(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  await handleEditAppointment(
                    editingAppointment.id,
                    editingAppointment.status,
                    editingAppointment.comments,
                  );
                  setIsEditModalOpen(false);
                  setEditingAppointment(null);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedAppointment(null);
        }}
        title="Appointment Details"
        size="lg"
      >
        {selectedAppointment &&
          (() => {
            const DetailPetIcon =
              PET_TYPE_ICONS[
                selectedAppointment.pet?.pet_type?.toLowerCase()
              ] || Star;
            return (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    marginBottom: "24px",
                    paddingBottom: "16px",
                    borderBottom: "1px solid #E5E7EB",
                  }}
                >
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "50%",
                      backgroundColor: "#F3F4F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DetailPetIcon
                      style={{
                        width: "28px",
                        height: "28px",
                        color: "#6B7280",
                      }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#111827",
                      }}
                    >
                      {selectedAppointment.pet?.name || "Unknown"}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6B7280" }}>
                      {getPetTypeLabel(selectedAppointment.pet?.pet_type)} •{" "}
                      {selectedAppointment.pet?.breed?.replace(/_/g, " ") ||
                        "Unknown breed"}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                    marginBottom: "24px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginBottom: "4px",
                      }}
                    >
                      Owner
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#111827",
                      }}
                    >
                      {`${selectedAppointment.pet?.user?.first_name || ""} ${selectedAppointment.pet?.user?.last_name || ""}`.trim() ||
                        "Unknown"}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginBottom: "4px",
                      }}
                    >
                      Service
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#111827",
                      }}
                    >
                      {selectedAppointment.service?.name || "Service"}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginBottom: "4px",
                      }}
                    >
                      Date & Time
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#111827",
                      }}
                    >
                      {new Date(
                        selectedAppointment.datetime,
                      ).toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6B7280" }}>
                      {new Date(
                        selectedAppointment.datetime,
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginBottom: "4px",
                      }}
                    >
                      Status
                    </div>
                    <div
                      style={{
                        display: "inline-flex",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "14px",
                        fontWeight: "500",
                        backgroundColor:
                          selectedAppointment.status === "confirmed"
                            ? "#D1FAE5"
                            : selectedAppointment.status === "pending"
                              ? "#FEF3C7"
                              : selectedAppointment.status === "cancelled"
                                ? "#FEE2E2"
                                : selectedAppointment.status === "completed"
                                  ? "#E5E7EB"
                                  : "#F3F4F6",
                        color:
                          selectedAppointment.status === "confirmed"
                            ? "#059669"
                            : selectedAppointment.status === "pending"
                              ? "#D97706"
                              : selectedAppointment.status === "cancelled"
                                ? "#DC2626"
                                : selectedAppointment.status === "completed"
                                  ? "#4B5563"
                                  : "#6B7280",
                      }}
                    >
                      {selectedAppointment.status?.charAt(0).toUpperCase() +
                        selectedAppointment.status?.slice(1) || "Pending"}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginBottom: "4px",
                      }}
                    >
                      Fee
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#111827",
                      }}
                    >
                      ${selectedAppointment.fee?.toFixed(2) || "0.00"}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginBottom: "4px",
                      }}
                    >
                      Appointment ID
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#111827",
                      }}
                    >
                      #{selectedAppointment.id}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#111827",
                      marginBottom: "12px",
                    }}
                  >
                    Pet Details
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "12px",
                      padding: "12px",
                      backgroundColor: "#F9FAFB",
                      borderRadius: "8px",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "12px", color: "#6B7280" }}>
                        Gender
                      </div>
                      <div style={{ fontSize: "14px", color: "#111827" }}>
                        {selectedAppointment.pet?.gender
                          ?.charAt(0)
                          .toUpperCase() +
                          selectedAppointment.pet?.gender?.slice(1) ||
                          "Unknown"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "12px", color: "#6B7280" }}>
                        Weight
                      </div>
                      <div style={{ fontSize: "14px", color: "#111827" }}>
                        {selectedAppointment.pet?.weight
                          ? `${selectedAppointment.pet.weight} kg`
                          : "Unknown"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "12px", color: "#6B7280" }}>
                        Height
                      </div>
                      <div style={{ fontSize: "14px", color: "#111827" }}>
                        {selectedAppointment.pet?.height
                          ? `${selectedAppointment.pet.height} cm`
                          : "Unknown"}
                      </div>
                    </div>
                  </div>
                </div>

                {(selectedAppointment.comments ||
                  selectedAppointment.reason) && (
                  <div style={{ marginBottom: "24px" }}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#111827",
                        marginBottom: "8px",
                      }}
                    >
                      Notes
                    </div>
                    <div
                      style={{
                        padding: "12px",
                        backgroundColor: "#F9FAFB",
                        borderRadius: "8px",
                        fontSize: "14px",
                        color: "#374151",
                      }}
                    >
                      {selectedAppointment.comments ||
                        selectedAppointment.reason}
                    </div>
                  </div>
                )}

                <div style={s.formActions}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsDetailModalOpen(false);
                      setSelectedAppointment(null);
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setIsDetailModalOpen(false);
                      setEditingAppointment(selectedAppointment);
                      setIsEditModalOpen(true);
                      setSelectedAppointment(null);
                    }}
                  >
                    Edit Appointment
                  </Button>
                </div>
              </div>
            );
          })()}
      </Modal>
    </div>
  );
};

export default AppointmentsScreen;
